import { useState, useEffect } from 'react';
import { getPublicClientOrNull } from '../../../../../../api/companyRecords';
import { patchStorefrontCalendarIntegrations } from '../../../../../../api/storefront/calendar-sync/calendarSync';
import { syncWithGoogleCalendar } from '../calendarSyncClient';
import { syncWithOutlookCalendar, connectOutlookCalendar, disconnectOutlookCalendar } from '../outlookSync';
import { googleConfig } from './calendarConfig';

export const useCalendarSync = (userId, appointments = []) => {
  const [syncStatus, setSyncStatus] = useState({
    google: { connected: false, loading: false, error: null },
    outlook: { connected: false, loading: false, error: null },
  });
  const [clientUser, setClientUser] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchClientData = async () => {
      if (!userId) return;

      try {
        const companyId = localStorage.getItem('clientCompanyId');
        if (!companyId) {
          throw new Error('Company ID not found');
        }

        const clientData = await getPublicClientOrNull({ companyId, id: userId });
        if (!clientData) {
          throw new Error('Client document not found');
        }
        if (cancelled) return;

        setClientUser({
          id: userId,
          companyId,
          ...clientData,
        });

        const googleConnected = !!clientData?.calendarIntegrations?.google;
        const outlookConnected = !!clientData?.calendarIntegrations?.outlook;

        setSyncStatus((prev) => ({
          google: { ...prev.google, connected: googleConnected, error: null },
          outlook: { ...prev.outlook, connected: outlookConnected, error: null },
          calendarIntegrations: clientData?.calendarIntegrations,
        }));
      } catch (error) {
        if (cancelled) return;
        setSyncStatus((prev) => ({
          google: { ...prev.google, error: error.message },
          outlook: { ...prev.outlook, error: error.message },
        }));
      }
    };

    fetchClientData();
    const interval = setInterval(fetchClientData, 12000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [userId]);

  const applyConnection = (provider, connected, extra = {}) => {
    setSyncStatus((prev) => ({
      ...prev,
      [provider]: { connected, loading: false, error: extra.error || null },
      calendarIntegrations: {
        ...(prev.calendarIntegrations || {}),
        [provider]: connected,
      },
    }));
  };

  const syncAppointment = async (appointment, provider) => {
    try {
      if (!clientUser?.id || !clientUser?.companyId) {
        throw new Error('User information not available');
      }

      const clientData = await getPublicClientOrNull({
        companyId: clientUser.companyId,
        id: clientUser.id,
      });
      if (!clientData?.calendarIntegrations?.[provider]) {
        throw new Error(`${provider} calendar is not connected`);
      }

      setSyncStatus((prev) => ({
        ...prev,
        [provider]: { ...prev[provider], loading: true, error: null },
      }));

      const appointmentData = {
        ...appointment,
        clientName: `${clientUser.firstName || ''} ${clientUser.lastName || ''}`.trim() || clientUser.name,
        clientEmail: clientUser.email,
        clientPhone: clientUser.phone,
        companyId: clientUser.companyId,
      };

      let syncResult;
      if (provider === 'google') {
        syncResult = await syncWithGoogleCalendar(
          appointmentData,
          clientUser.id,
          false,
          clientUser.companyId
        );
      } else if (provider === 'outlook') {
        syncResult = await syncWithOutlookCalendar(appointmentData, clientUser.id, Boolean(appointment.outlookEventId));
      } else {
        throw new Error(`Unsupported calendar provider: ${provider}`);
      }

      if (!syncResult.success) {
        throw new Error(syncResult.error || `Failed to sync with ${provider}`);
      }

      setSyncStatus((prev) => ({
        ...prev,
        [provider]: { ...prev[provider], loading: false, error: null },
      }));

      return { success: true, eventId: syncResult.eventId };
    } catch (error) {
      setSyncStatus((prev) => ({
        ...prev,
        [provider]: { ...prev[provider], loading: false, error: error.message },
      }));
      return { success: false, error: error.message };
    }
  };

  const disconnectCalendar = async (provider) => {
    try {
      if (!clientUser?.id || !clientUser?.companyId) {
        throw new Error('User information not available');
      }

      setSyncStatus((prev) => ({
        ...prev,
        [provider]: { ...prev[provider], loading: true, error: null },
      }));

      if (provider === 'google') {
        const token = localStorage.getItem('googleCalendarToken');
        if (token && window.google?.accounts?.oauth2?.revoke) {
          await new Promise((resolve) => {
            window.google.accounts.oauth2.revoke(token, () => resolve());
          });
        }
        localStorage.removeItem('googleCalendarToken');
        localStorage.removeItem('googleCalendarTokenExpiry');
        await patchStorefrontCalendarIntegrations({
          companyId: clientUser.companyId,
          clientId: clientUser.id,
          google: false,
        });
        applyConnection('google', false);
        return { success: true };
      }

      if (provider === 'outlook') {
        const result = await disconnectOutlookCalendar(clientUser.id, clientUser.companyId);
        if (!result.success) {
          throw new Error(result.error);
        }
        applyConnection('outlook', false);
        return result;
      }

      throw new Error(`Unsupported calendar provider: ${provider}`);
    } catch (error) {
      setSyncStatus((prev) => ({
        ...prev,
        [provider]: { ...prev[provider], loading: false, error: error.message },
      }));
      return { success: false, error: error.message };
    }
  };

  const connectCalendar = async (provider) => {
    try {
      if (!clientUser?.id || !clientUser?.companyId) {
        throw new Error('User information not available');
      }

      setSyncStatus((prev) => ({
        ...prev,
        [provider]: { ...prev[provider], loading: true, error: null },
      }));

      if (provider === 'google') {
        if (!googleConfig.clientId) {
          throw new Error('Google Client ID is not configured. Please check your environment variables.');
        }
        if (!googleConfig.apiKey) {
          throw new Error('Google API Key is not configured. Please check your environment variables.');
        }

        if (typeof window.gapi === 'undefined') {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://apis.google.com/js/api.js';
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Google API client'));
            document.head.appendChild(script);
          });
        }

        try {
          await new Promise((resolve, reject) => {
            window.gapi.load('client', {
              callback: () => resolve(),
              onerror: () => reject(new Error('Failed to load GAPI client')),
            });
          });
          await window.gapi.client.init({
            apiKey: googleConfig.apiKey,
            discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'],
          });
        } catch (error) {
          // Continue with OAuth flow even if GAPI initialization fails
        }

        if (typeof window.google === 'undefined' || !window.google.accounts) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
            document.head.appendChild(script);
          });
        }

        const result = await new Promise((resolve, reject) => {
          try {
            if (!window.google?.accounts?.oauth2) {
              reject(new Error('Google Identity Services not properly loaded'));
              return;
            }

            const tokenClient = window.google.accounts.oauth2.initTokenClient({
              client_id: googleConfig.clientId,
              scope: googleConfig.scopes.join(' '),
              prompt: 'consent',
              callback: async (tokenResponse) => {
                try {
                  if (tokenResponse.error) {
                    reject(new Error(tokenResponse.error_description || tokenResponse.error));
                    return;
                  }

                  localStorage.setItem('googleCalendarToken', tokenResponse.access_token);
                  localStorage.setItem(
                    'googleCalendarTokenExpiry',
                    new Date(Date.now() + tokenResponse.expires_in * 1000).toISOString()
                  );

                  await patchStorefrontCalendarIntegrations({
                    companyId: clientUser.companyId,
                    clientId: clientUser.id,
                    google: true,
                  });

                  applyConnection('google', true);
                  resolve({ success: true });
                } catch (error) {
                  reject(error);
                }
              },
            });

            tokenClient.requestAccessToken({ prompt: 'consent' });
          } catch (error) {
            reject(error);
          }
        });

        return result;
      }

      if (provider === 'outlook') {
        const result = await connectOutlookCalendar(clientUser.id, clientUser.companyId);
        if (!result.success) {
          throw new Error(result.error);
        }
        applyConnection('outlook', true);
        return result;
      }

      throw new Error(`Unsupported calendar provider: ${provider}`);
    } catch (error) {
      setSyncStatus((prev) => ({
        ...prev,
        [provider]: { ...prev[provider], loading: false, error: error.message },
      }));
      return { success: false, error: error.message };
    }
  };

  return {
    syncStatus,
    syncAppointment,
    disconnectCalendar,
    connectCalendar,
    clientUser,
    setSyncStatus,
    appointments,
  };
};
